import React from 'react';
import { useChildren } from '../context/ChildProvider';
import { calculateAge } from '../helpers/utils';
import { generatePdfFromElement, downloadOrSharePDF } from '../helpers/pdfGenerator';
import { Button } from '../components/UI';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function RecapScreen({ navigate, user }) {
    const { myChildren, sharedChildren } = useChildren();
    const [filterSchool, setFilterSchool] = React.useState('');
    const [filterClass, setFilterClass] = React.useState('');

    const allPatients = [...sharedChildren, ...myChildren];
    const uniqueSchools = Array.from(new Set(allPatients.map(c => c.schoolName).filter(Boolean)));
    const uniqueClasses = Array.from(new Set(allPatients.map(c => c.className).filter(Boolean)));

    const filteredPatients = allPatients.filter(child => {
        if (filterSchool && child.schoolName !== filterSchool) return false;
        if (filterClass && child.className !== filterClass) return false;
        return true;
    });

    const handleDeleteSharedChild = async (childId) => {
        if (window.confirm('Apakah Anda yakin ingin menghapus akses ke data anak ini?')) {
            try {
                await deleteDoc(doc(db, `users/${user.uid}/sharedAccess/${childId}`));
            } catch (error) {
                console.error('Error deleting shared access:', error);
                alert('Gagal menghapus akses.');
            }
        }
    };

    const getStatusInfo = (child) => {
        const lastAssessment = child.lastAssessment;
        if (!lastAssessment || !lastAssessment.overallStatus) {
            return { text: 'Belum Diperiksa', color: 'gray', advice: 'Lakukan skrining segera.' };
        }
        switch (lastAssessment.overallStatus) {
            case 'Sesuai': 
            case 'Normal':
                return { text: 'Normal', color: 'emerald', advice: 'Lanjutkan stimulasi sesuai umur.' };
            case 'Meragukan': 
                return { text: 'Meragukan', color: 'amber', advice: 'Ulangi skrining 2 minggu lagi. Beri stimulasi lebih sering.' };
            case 'Ada Penyimpangan': 
            case 'Penyimpangan':
                return { text: 'Penyimpangan', color: 'rose', advice: 'Lakukan intervensi spesifik sesuai hasil rujukan.' };
            case 'Perlu Rujukan Segera':
                return { text: 'Rujukan Segera', color: 'red', advice: 'Rujuk ke faskes rujukan tingkat lanjut (RS/Poli Tumbuh Kembang).' };
            default: 
                return { text: lastAssessment.overallStatus, color: 'cyan', advice: '-' };
        }
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Laporan Rekapan Skrining Tumbuh Kembang Anak", 14, 15);
        doc.setFontSize(10);
        doc.text(`Dicetak oleh: ${user.username || user.email} (${user.role})`, 14, 22);
        
        const tableColumn = ["Nama Anak", "Sekolah - Kelas", "Usia", "Status Terakhir", "Rekomendasi/Tindak Lanjut"];
        const tableRows = [];

        filteredPatients.forEach(child => {
            const age = calculateAge(child.dob.toDate());
            const statusInfo = getStatusInfo(child);
            const schoolClass = `${child.schoolName || '-'} / ${child.className || '-'}`;
            const childData = [
                child.name,
                schoolClass,
                age,
                statusInfo.text,
                statusInfo.advice
            ];
            tableRows.push(childData);
        });

        (doc as any).autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 28,
        });

        downloadOrSharePDF(doc, `Rekapan_Pasien_${new Date().getTime()}.pdf`);
    };

    const handleExportExcel = () => {
        const dataToExport = filteredPatients.map(child => {
            const age = calculateAge(child.dob.toDate());
            const statusInfo = getStatusInfo(child);
            return {
                "Nama Anak": child.name,
                "Asal Sekolah / PAUD": child.schoolName || '-',
                "Kelas": child.className || '-',
                "Usia": age,
                "Jenis Kelamin": child.gender,
                "Status Pemeriksaan Terakhir": statusInfo.text,
                "Rekomendasi / Tindak Lanjut": statusInfo.advice
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Rekapan Pasien");
        
        XLSX.writeFile(workbook, `Rekapan_Pasien_${new Date().getTime()}.xlsx`);
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-purple-200 pb-28 pt-8 px-5 relative">
            {/* Background elements */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-sky-300 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[20%] right-[-10%] w-[40vw] h-[40vw] bg-indigo-300 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute inset-0 bg-white/30 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <button onClick={() => navigate('home')} className="text-gray-500 hover:text-gray-800 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight text-center flex-grow">Rekapan Pasien</h2>
                    <div className="w-6"></div> {/* Spacer for centering */}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <p className="text-sm text-gray-500">Menampilkan {filteredPatients.length} dari {allPatients.length} data anak.</p>
                    <div className="flex gap-2">
                        <Button onClick={handleExportPDF} className="bg-red-500 text-white hover:bg-red-600 shadow-md flex items-center gap-2">
                            <span>📄</span> Export PDF
                        </Button>
                        <Button onClick={handleExportExcel} className="bg-emerald-500 text-white hover:bg-emerald-600 shadow-md flex items-center gap-2">
                            <span>📊</span> Export Excel
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <select value={filterSchool} onChange={e => setFilterSchool(e.target.value)} className="bg-white/80 border border-gray-200 text-gray-700 text-sm rounded-xl p-2.5 outline-none shadow-sm flex-1 font-semibold">
                        <option value="">Semua Sekolah / PAUD</option>
                        {uniqueSchools.map(school => (
                            <option key={school} value={school}>{school}</option>
                        ))}
                    </select>
                    <select value={filterClass} onChange={e => setFilterClass(e.target.value)} className="bg-white/80 border border-gray-200 text-gray-700 text-sm rounded-xl p-2.5 outline-none shadow-sm flex-1 font-semibold">
                        <option value="">Semua Kelas</option>
                        {uniqueClasses.map(cls => (
                            <option key={cls} value={cls}>{cls}</option>
                        ))}
                    </select>
                </div>

                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-6 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-x-auto"
                >
                    {allPatients.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 bg-gray-50/50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider rounded-tl-xl">Nama Anak</th>
                                    <th className="px-6 py-3 bg-gray-50/50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Sekolah & Kelas</th>
                                    <th className="px-6 py-3 bg-gray-50/50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Usia</th>
                                    <th className="px-6 py-3 bg-gray-50/50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status (Warning)</th>
                                    <th className="px-6 py-3 bg-gray-50/50 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Rekomendasi</th>
                                    <th className="px-6 py-3 bg-gray-50/50 text-center text-xs font-bold text-gray-500 uppercase tracking-wider rounded-tr-xl">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white/40 divide-y divide-gray-200">
                                {filteredPatients.map((child, index) => {
                                    const statusInfo = getStatusInfo(child);
                                    const age = calculateAge(child.dob.toDate());
                                    return (
                                        <motion.tr 
                                            key={child.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="hover:bg-white/60 transition-colors"
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-bold text-gray-900">{child.name}</div>
                                                <div className="text-xs text-gray-500">{child.gender}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-700">{child.schoolName || '-'}</div>
                                                <div className="text-xs text-gray-500">{child.className ? `Kelas: ${child.className}` : '-'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {age}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-${statusInfo.color}-100 text-${statusInfo.color}-800 border border-${statusInfo.color}-200`}>
                                                    {statusInfo.text}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500 max-w-xs break-words">
                                                {statusInfo.advice}
                                            </td>
                                            <td className="px-6 py-4 text-center whitespace-nowrap">
                                                {child.isShared && (
                                                    <button 
                                                        onClick={() => handleDeleteSharedChild(child.id)}
                                                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-2 rounded-lg transition-colors"
                                                        title="Hapus Akses"
                                                    >
                                                        🗑️
                                                    </button>
                                                )}
                                            </td>
                                        </motion.tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-gray-500">Belum ada data pasien untuk direkap.</p>
                        </div>
                    )}
                </motion.div>
            </div>
            
            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
            `}</style>
        </div>
    );
}
